import { useEffect } from "react";
import { useSelector } from "react-redux";
import { createSlice } from "@reduxjs/toolkit";
import { getStore } from "../../storeRef";

//--------------------------------------------------------------------------------------
//  For redux service for applciation scoped (data wont change in application lifetime)
//--------------------------------------------------------------------------------------
export function createApplicationService({
  name,
  apiFetchFn,
  apiAddFn,
  apiUpdateFn,
  initialData = [],
}) {
  const slice = createSlice({
    name,
    initialState: {
      data: initialData,
      fetched: false,
      loading: true,
    },
    reducers: {
      setLoading(state, action) {
        state.loading = action.payload;
      },
      setData(state, action) {
        state.data = action.payload;
        state.fetched = true;
        state.loading = false;
      },
      resetFetched(state) {
        state.fetched = false;
        state.loading = true;
      },
    },
  });

  const { setData, setLoading } = slice.actions;
  const reducer = slice.reducer;

  let fetchPromise = null;

  const _fetchData = () => async (dispatch) => {
    dispatch(setLoading(true));

    try {
      const result = await apiFetchFn();
      dispatch(setData(result.data.data));
      return result.data.data;
    } catch (error) {
      dispatch(setLoading(false));
      return undefined;
    }
  };

  const getData = async () => {
    const store = getStore();
    const state = store.getState();
    if (!state[name].fetched) {
      if (!fetchPromise) {
        fetchPromise = store.dispatch(_fetchData()).finally(() => {
          fetchPromise = null;
        });
      }
      await fetchPromise;
    }
    return getStore().getState()[name].data;
  };

  const addData = async (data) => {
    try {
      await apiAddFn(data);
      getStore().dispatch(_fetchData());
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updateData = async (id, data) => {
    try {
      await apiUpdateFn(id, data);
      getStore().dispatch(_fetchData());
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const useData = () => {
    const data = useSelector((state) => state[name].data);
    const fetched = useSelector((state) => state[name].fetched);
    const loading = useSelector((state) => state[name].loading);
    useEffect(() => {
      if (!fetched) {
        getData();
      }
    }, [fetched]);
    return { data, loading, fetched };
  };

  const resetData = () => {
    getStore().dispatch(slice.actions.resetFetched());
  };

  return {
    reducer,
    getData,
    addData,
    updateData,
    useData,
    resetData,
  };
}
